// ────────────────────────────────────────────────────────────────────────────
//  AgenteAnalisis — consola flotante del analista de rendimiento.
//
//  Misma gramática visual que AgentBubble (el agente del Centro de Mensajes): orbe
//  oscuro con anillos girando, halo que late, barrido de vidrio y deriva de tono según
//  el ánimo. Se comparte a propósito — son el mismo producto y tienen que sentirse
//  hermanos; lo que cambia es el trabajo, no la criatura.
//
//  Los estados son los de un analista, no los de un operador: escuchando (esperando la
//  pregunta), consultando (mirando los datos) y respondiendo. La VELOCIDAD hace la mitad
//  del mensaje: escuchando deriva lento y frío, consultando se acelera y se satura.
//
//  El panel se abre al costado de la burbuja y muestra, debajo de cada respuesta, qué
//  herramientas consultó. Eso no es adorno: es lo único que distingue un número que salió
//  de la base de uno que el modelo dedujo.
//
//  El motor está en `tcdg-api`: AnalyticsAgent + AnalyticsTools.
// ────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";

import { API_URI } from "../../helpers";
/**
 * La sesión del panel, tal cual la dejó el login.
 *
 * `POST /admin/login` guarda el token en la cookie `api`. Se lee de ahí y se manda como
 * header porque el panel vive en `admin.` y la API en `api.`: una cookie host-only del
 * panel no viaja sola a la API.
 *
 * No se usa un secreto compartido en localStorage: si estás logueado en el panel, entrás.
 */
function tokenDeSesion() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)api=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : "";
  } catch (e) {
    return "";
  }
}

// ── Estados y su ánimo ──────────────────────────────────────────────────────
const ESTADO = {
  escuchando: {
    label: "Escuchando",
    hint: "Preguntame sobre los datos",
    animo: "tranquilo",
    color: "#22D3EE", accent: "#67E8F9", glow: "rgba(34,211,238,0.45)",
    tri: ["#67E8F9", "#34D399", "#38BDF8"],
  },
  consultando: {
    label: "Consultando",
    hint: "Mirando los datos de verdad",
    animo: "enfocado",
    color: "#818CF8", accent: "#A5B4FC", glow: "rgba(129,140,248,0.6)",
    tri: ["#A5B4FC", "#F472B6", "#22D3EE"],
  },
  respondio: {
    label: "Respondió",
    hint: "Con el dato consultado",
    animo: "esperandote",
    color: "#FBBF24", accent: "#FCD34D", glow: "rgba(251,191,36,0.5)",
    tri: ["#FCD34D", "#FB923C", "#F472B6"],
  },
};

const BUBBLE = 74;

const P = {
  send: "M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z",
  close: "M18 6L6 18M6 6l12 12",
  broom: "M3 21l6-6M14 4l6 6-9 9H5v-6l9-9z",
};

const Ico = ({ name, size = 15, color = "currentColor", w = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth={w} strokeLinecap="round" strokeLinejoin="round">
    <path d={P[name]} />
  </svg>
);

// ── Núcleo animado ──────────────────────────────────────────────────────────
// Los anillos giran con SMIL y no con keyframes CSS: anda en Safari sin depender del
// motor de animación de JSS. Mismo criterio que el Core del agente del inbox.
const Core = ({ estado, size = 46 }) => {
  const s = ESTADO[estado];
  const spin = estado === "consultando" ? 2.2 : estado === "respondio" ? 3.4 : 7;

  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" style={{ display: "block" }}>
      <defs>
        {/* Tres paradas y no dos: es lo que hace que el giro se lea como color
            moviéndose y no como un tinte plano cambiando. */}
        <linearGradient id={`an-g-${estado}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={s.tri[0]} />
          <stop offset="45%" stopColor={s.tri[1]} />
          <stop offset="100%" stopColor={s.tri[2]} stopOpacity="0.35" />
        </linearGradient>
        <linearGradient id={`an-h-${estado}`} x1="1" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={s.tri[2]} />
          <stop offset="50%" stopColor={s.tri[0]} />
          <stop offset="100%" stopColor={s.tri[1]} />
        </linearGradient>
      </defs>

      {/* Anillo exterior */}
      <g opacity={0.95}>
        <circle cx="50" cy="50" r="38" stroke={`url(#an-g-${estado})`} strokeWidth="2.5"
                strokeLinecap="round" strokeDasharray="60 179">
          <animateTransform attributeName="transform" type="rotate"
                            from="0 50 50" to="360 50 50" dur={`${spin}s`} repeatCount="indefinite" />
        </circle>
        <circle cx="50" cy="50" r="38" stroke={s.color} strokeWidth="1" opacity="0.25" />
      </g>

      {/* Anillo medio: al revés y más lento */}
      <g opacity={0.85}>
        <circle cx="50" cy="50" r="27" stroke={`url(#an-h-${estado})`} strokeWidth="2.5"
                strokeLinecap="round" strokeDasharray="34 136">
          <animateTransform attributeName="transform" type="rotate"
                            from="360 50 50" to="0 50 50" dur={`${spin * 1.9}s`} repeatCount="indefinite" />
        </circle>
      </g>

      {/* Partículas en órbita */}
      {[0, 120, 240].map((deg, i) => (
        <circle key={i} cx="50" cy="12" r={i === 0 ? 3.2 : 2.2}
                fill={s.tri[i]} opacity={0.95 - i * 0.15}>
          <animateTransform attributeName="transform" type="rotate"
                            from={`${deg} 50 50`} to={`${deg + 360} 50 50`}
                            dur={`${spin * (1.3 + i * 0.45)}s`} repeatCount="indefinite" />
        </circle>
      ))}

      {/* Núcleo */}
      <circle cx="50" cy="50" r="13" fill={`url(#an-h-${estado})`} opacity={0.95} />
      <circle cx="50" cy="50" r="13" fill="none" stroke={s.accent} strokeWidth="1.5" opacity="0.7" />
      <circle cx="50" cy="50" r="13" fill="none" stroke={s.accent} strokeWidth="2" opacity="0.55">
        <animate attributeName="r" values="13;26;13" dur="2.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.55;0;0.55" dur="2.6s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
};

const useStyles = makeStyles({
  "@keyframes anFloat": {
    "0%,100%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-5px)" },
  },
  "@keyframes anSheen": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  "@keyframes anAnimoTranquilo": {
    "0%, 100%": { filter: "hue-rotate(-18deg) saturate(1)" },
    "50%": { filter: "hue-rotate(18deg) saturate(1.15)" },
  },
  "@keyframes anAnimoEnfocado": {
    "0%": { filter: "hue-rotate(-55deg) saturate(1.25)" },
    "50%": { filter: "hue-rotate(55deg) saturate(1.55)" },
    "100%": { filter: "hue-rotate(-55deg) saturate(1.25)" },
  },
  "@keyframes anAnimoEsperandote": {
    "0%, 100%": { filter: "hue-rotate(-12deg) saturate(1.3)" },
    "50%": { filter: "hue-rotate(12deg) saturate(1.85)" },
  },
  "@keyframes anHalo": {
    "0%, 100%": { transform: "scale(0.92)", opacity: 0.55 },
    "50%": { transform: "scale(1.18)", opacity: 0.95 },
  },
  "@keyframes anIn": {
    from: { opacity: 0, transform: "translateY(6px) scale(0.97)" },
    to: { opacity: 1, transform: "none" },
  },

  layer: {
    position: "fixed",
    zIndex: 1400,
    pointerEvents: "none",
    fontFamily: "-apple-system,BlinkMacSystemFont,'Inter','Segoe UI',Roboto,sans-serif",
  },
  stack: { position: "relative", display: "flex", alignItems: "flex-start", pointerEvents: "auto" },

  bubbleWrap: {
    position: "relative",
    width: BUBBLE, height: BUBBLE,
    flexShrink: 0,
    animation: "$anFloat 5.5s ease-in-out infinite",
  },
  bubbleWrapStill: { animation: "none" },
  halo: {
    position: "absolute", inset: -18,
    borderRadius: "50%",
    pointerEvents: "none",
    zIndex: -1,
    animation: "$anHalo 4s ease-in-out infinite",
  },
  haloTranquilo: { animationDuration: "5.5s" },
  haloEnfocado: { animationDuration: "3s" },
  haloEsperandote: { animationDuration: "1.6s" },
  animoTranquilo: { animation: "$anAnimoTranquilo 15s ease-in-out infinite" },
  animoEnfocado: { animation: "$anAnimoEnfocado 7s ease-in-out infinite" },
  animoEsperandote: { animation: "$anAnimoEsperandote 3.4s ease-in-out infinite" },

  bubble: {
    position: "relative",
    width: BUBBLE, height: BUBBLE,
    borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "grab",
    userSelect: "none",
    overflow: "hidden",
    background: "radial-gradient(120% 120% at 30% 22%, #1E293B 0%, #0B1220 55%, #060B14 100%)",
    border: "1px solid rgba(255,255,255,0.10)",
    transition: "box-shadow .35s, transform .2s",
    touchAction: "none",
  },
  bubbleDragging: { cursor: "grabbing", transform: "scale(1.05)" },
  sheen: {
    position: "absolute", inset: -BUBBLE * 0.5,
    background: "conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.10) 40deg, transparent 120deg)",
    animation: "$anSheen 9s linear infinite",
    pointerEvents: "none",
  },
  glassTop: {
    position: "absolute", top: 4, left: 12, right: 12, height: "42%",
    borderRadius: "50%",
    background: "linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0))",
    pointerEvents: "none",
  },

  // ── Panel ────────────────────────────────────────────────
  /**
   * El panel cuelga de la burbuja con posición ABSOLUTA, no como hermano flex.
   *
   * Con `flex-direction: row-reverse` el panel se dibujaba a la izquierda de la burbuja pero
   * la caja seguía anclada por `left` y creciendo hacia la derecha: con la burbuja abajo a
   * la derecha, el panel terminaba mitad afuera de la pantalla. Sacándolo del flujo se
   * ancla por el lado que corresponde (`left` o `right`) y no arrastra la caja.
   */
  panel: {
    position: "absolute",
    width: 420,
    background: "#0B1220",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 16,
    boxShadow: "0 20px 60px rgba(2,6,23,0.55)",
    color: "#E2E8F0",
    overflow: "hidden",
    animation: "$anIn .2s ease-out",
    display: "flex",
    flexDirection: "column",
    maxHeight: "min(70vh, 620px)",
  },
  /**
   * En mobile el panel deja de colgar de la burbuja y pasa a ser una hoja anclada abajo,
   * a todo el ancho. Colgado se iba de pantalla apenas la burbuja estaba en la mitad
   * derecha, que es donde cae por defecto.
   *
   * `100dvh` y no `100vh`: en iOS la barra del navegador entra y sale, y con vh el panel
   * queda tapado por la barra justo donde está el campo de escribir. El `env(safe-area…)`
   * es por el notch y la barra de gestos.
   */
  panelMobile: {
    position: "fixed",
    left: 8, right: 8, bottom: 8,
    top: "auto",
    width: "auto",
    maxHeight: "min(72dvh, 560px)",
    paddingBottom: "env(safe-area-inset-bottom, 0px)",
  },
  head: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "12px 14px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },
  headTitle: { fontSize: 14, fontWeight: 700, letterSpacing: 0.2 },
  headHint: { fontSize: 11, color: "#94A3B8" },
  iconBtn: {
    background: "transparent", border: "none", color: "#94A3B8",
    cursor: "pointer", padding: 6, borderRadius: 8, display: "flex",
  },
  body: { padding: 14, overflowY: "auto", flex: 1 },

  fila: { display: "flex", marginBottom: 12 },
  burbujaMia: {
    marginLeft: "auto",
    maxWidth: "85%",
    background: "rgba(129,140,248,0.18)",
    border: "1px solid rgba(129,140,248,0.35)",
    color: "#E0E7FF",
    borderRadius: 12,
    padding: "9px 12px",
    fontSize: 13.5,
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
  },
  burbujaSuya: {
    maxWidth: "88%",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12,
    padding: "10px 13px",
    fontSize: 13.5,
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
  },
  chips: { marginTop: 9, display: "flex", flexWrap: "wrap", gap: 5 },
  chip: {
    fontSize: 10.5,
    color: "#94A3B8",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 999,
    padding: "2px 8px",
  },
  sugerencia: {
    display: "block", width: "100%", textAlign: "left",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "#CBD5E1",
    borderRadius: 10, padding: "8px 11px", marginBottom: 7,
    fontSize: 12.5, cursor: "pointer",
    fontFamily: "inherit",
  },
  pie: { padding: 12, borderTop: "1px solid rgba(255,255,255,0.08)" },
  input: {
    width: "100%",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 10,
    color: "#E2E8F0",
    padding: "9px 11px",
    fontSize: 13.5,
    resize: "none",
    outline: "none",
    fontFamily: "inherit",
    // iOS hace zoom automático al enfocar un input de menos de 16px y después deja la
    // página escalada. Con 16px no pasa; en desktop se ve igual de bien.
    "@media (hover: none)": { fontSize: 16 },
  },
  nota: { fontSize: 10.5, color: "#64748B", marginTop: 8, lineHeight: 1.5 },
});

const SUGERENCIAS = [
  "¿Cómo viene el mes contra el anterior?",
  "¿Qué creativo de Meta rinde mejor esta semana?",
  "¿La caída de ventas es por menos tráfico o peor conversión?",
  "¿Cómo viene la secuencia de mails?",
];

export default function AgenteAnalisis() {
  const c = useStyles();
  const [pos, setPos] = useState(() => {
    try {
      const g = JSON.parse(localStorage.getItem("agenteAnalisisPos") || "null");
      if (g && typeof g.x === "number") return g;
    } catch (e) { /* sin preferencia guardada */ }
    return { x: window.innerWidth - BUBBLE - 34, y: window.innerHeight - BUBBLE - 34 };
  });
  const [abierto, setAbierto] = useState(false);
  const [turnos, setTurnos] = useState([]);
  const [texto, setTexto] = useState("");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  // Lo último que se preguntó, para reintentarlo solo apenas se carga el token.
  const ultima = useRef(null);
  const [dragging, setDragging] = useState(false);
  const drag = useRef({ on: false, dx: 0, dy: 0, moved: false });
  const finRef = useRef(null);

  // Táctil o pantalla angosta. `(hover: none)` es lo que distingue un dedo de un mouse
  // mejor que el ancho — una laptop con pantalla chica sigue siendo desktop.
  const [esMobile, setEsMobile] = useState(() => {
    try {
      return window.matchMedia("(hover: none)").matches || window.innerWidth < 720;
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    /**
     * Reubica la burbuja si quedó fuera de la pantalla y recalcula si es mobile.
     *
     * Sin esto, girar el teléfono o achicar la ventana deja la burbuja en una coordenada
     * que ya no existe: queda invisible y sin forma de recuperarla, porque la posición
     * está guardada en localStorage y vuelve igual de rota en la próxima visita.
     */
    const alCambiar = () => {
      try {
        setEsMobile(window.matchMedia("(hover: none)").matches || window.innerWidth < 720);
      } catch (e) { /* navegador viejo: se queda como estaba */ }
      setPos((p) => {
        const x = Math.max(6, Math.min(window.innerWidth - BUBBLE - 6, p.x));
        const y = Math.max(6, Math.min(window.innerHeight - BUBBLE - 6, p.y));
        return x === p.x && y === p.y ? p : { x, y };
      });
    };
    window.addEventListener("resize", alCambiar);
    window.addEventListener("orientationchange", alCambiar);
    alCambiar();
    return () => {
      window.removeEventListener("resize", alCambiar);
      window.removeEventListener("orientationchange", alCambiar);
    };
  }, []);

  /**
   * De qué lado y con qué borde se cuelga el panel en desktop.
   *
   * Se decide por dónde está la burbuja: si está en la mitad derecha, el panel se ancla por
   * `right` para crecer hacia la izquierda; si está en la mitad de abajo, se ancla por
   * `bottom` para crecer hacia arriba. Sin las dos cosas, la burbuja en su posición por
   * defecto (abajo a la derecha) abre el panel fuera de la pantalla.
   */
  const panelIzquierda = !esMobile && pos.x > window.innerWidth / 2;
  const panelArriba = !esMobile && pos.y > window.innerHeight / 2;
  const anclaPanel = esMobile ? undefined : {
    [panelIzquierda ? "right" : "left"]: BUBBLE + 14,
    [panelArriba ? "bottom" : "top"]: 0,
  };

  const estado = cargando ? "consultando"
    : (turnos.length && turnos[turnos.length - 1].quien === "assistant") ? "respondio"
    : "escuchando";
  const meta = ESTADO[estado];

  useEffect(() => {
    if (abierto && finRef.current) finRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turnos, cargando, abierto]);

  // ── Arrastre ────────────────────────────────────────────
  const onPointerDown = (e) => {
    drag.current = { on: true, dx: e.clientX - pos.x, dy: e.clientY - pos.y, moved: false };
    setDragging(true);
    // Guarda explícita y no `?.()`: el eslint de esta versión de CRA marca la llamada
    // opcional como expresión sin usar y rompe el build.
    if (e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };
  const onPointerMove = useCallback((e) => {
    if (!drag.current.on) return;
    const x = Math.max(6, Math.min(window.innerWidth - BUBBLE - 6, e.clientX - drag.current.dx));
    const y = Math.max(6, Math.min(window.innerHeight - BUBBLE - 6, e.clientY - drag.current.dy));
    // Un click que se movió tres píxeles sigue siendo un click: sin este umbral,
    // abrir el panel con el mouse un poco tembloroso se vuelve imposible.
    if (Math.abs(e.movementX) + Math.abs(e.movementY) > 2) drag.current.moved = true;
    setPos({ x, y });
  }, []);
  const onPointerUp = useCallback(() => {
    if (!drag.current.on) return;
    drag.current.on = false;
    setDragging(false);
    setPos((p) => {
      try { localStorage.setItem("agenteAnalisisPos", JSON.stringify(p)); } catch (e) { /* modo privado */ }
      return p;
    });
  }, []);
  useEffect(() => {
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  // ── Preguntar ───────────────────────────────────────────
  function preguntar(pregunta) {
    const q = (pregunta != null ? pregunta : texto).trim();
    if (!q || cargando) return;

    setError(null);
    setTexto("");
    ultima.current = q;
    // El historial se arma ANTES de sumar el turno nuevo: el backend recibe los turnos
    // previos y la pregunta por separado.
    const historial = turnos.map((t) => ({ role: t.quien, content: t.texto }));
    // Si se reintenta después de cargar el token, la pregunta ya está en pantalla y no
    // hay que duplicarla.
    setTurnos((prev) => (
      prev.length && prev[prev.length - 1].quien === "user" && prev[prev.length - 1].texto === q
        ? prev
        : prev.concat([{ quien: "user", texto: q }])
    ));
    setCargando(true);

    fetch(`${API_URI}/admin/insights/agente`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Api-Token": tokenDeSesion() },
      body: JSON.stringify({ pregunta: q, historial }),
    })
      .then((r) => r.json().then((j) => ({ ok: r.ok, status: r.status, j })))
      .then(({ ok, status, j }) => {
        if (status === 401 || status === 403) {
          // No hay secreto que pedirle al usuario: si la sesión no vale, lo que corresponde
          // es volver a entrar al panel.
          setError(j.mensaje || "Tu sesión venció. Volvé a entrar al panel.");
          return;
        }
        if (!ok || !j.success) {
          setError(j.mensaje || j.error || "No se pudo responder.");
          return;
        }
        setTurnos((prev) => prev.concat([
          { quien: "assistant", texto: j.respuesta, consultas: j.consultas || [] },
        ]));
      })
      .catch(() => setError("No se pudo conectar con la API."))
      .finally(() => setCargando(false));
  }

  return (
    <div className={c.layer} style={{ left: pos.x, top: pos.y }}>
      <div className={c.stack}>
        <div className={`${c.bubbleWrap} ${dragging || abierto ? c.bubbleWrapStill : ""}`}>
          <span
            className={`${c.halo} ${
              meta.animo === "enfocado" ? c.haloEnfocado
              : meta.animo === "esperandote" ? c.haloEsperandote
              : c.haloTranquilo}`}
            style={{ background: `radial-gradient(circle, ${meta.glow} 0%, rgba(0,0,0,0) 68%)` }}
          />
          <div
            className={`${c.bubble} ${dragging ? c.bubbleDragging : ""} ${
              meta.animo === "enfocado" ? c.animoEnfocado
              : meta.animo === "esperandote" ? c.animoEsperandote
              : c.animoTranquilo}`}
            style={{ boxShadow: `0 10px 30px rgba(15,23,42,0.28), 0 0 0 1px rgba(255,255,255,0.05) inset, 0 0 26px ${meta.glow}` }}
            onPointerDown={onPointerDown}
            onClick={() => { if (!drag.current.moved) setAbierto((v) => !v); }}
            title={`Analista: ${meta.label} — ${meta.hint}. Arrastrá para mover.`}
            role="button"
            tabIndex={0}
            aria-label={`Agente de análisis: ${meta.label}`}
          >
            <div className={c.sheen} />
            <Core estado={estado} size={46} />
            <div className={c.glassTop} />
          </div>
        </div>

        {abierto && (
          <div
            className={`${c.panel} ${esMobile ? c.panelMobile : ""}`}
            style={anclaPanel}
          >
            <div className={c.head}>
              <div style={{ flex: 1 }}>
                <div className={c.headTitle} style={{ color: meta.color }}>
                  Analista · {meta.label}
                </div>
                <div className={c.headHint}>{meta.hint}</div>
              </div>
              {turnos.length > 0 && (
                <button className={c.iconBtn} onClick={() => setTurnos([])} title="Limpiar la charla">
                  <Ico name="broom" />
                </button>
              )}
              <button className={c.iconBtn} onClick={() => setAbierto(false)} title="Cerrar">
                <Ico name="close" />
              </button>
            </div>

            <div className={c.body}>
                            {turnos.length === 0 && !cargando && (
                <div>
                  <div style={{ fontSize: 12.5, color: "#94A3B8", marginBottom: 12, lineHeight: 1.6 }}>
                    Consulto los datos de verdad antes de responder. Abajo de cada respuesta vas a
                    ver qué miré.
                  </div>
                  {SUGERENCIAS.map((s) => (
                    <button key={s} className={c.sugerencia} onClick={() => preguntar(s)}>{s}</button>
                  ))}
                </div>
              )}

              {turnos.map((t, i) => (
                <div key={i} className={c.fila}>
                  <div className={t.quien === "user" ? c.burbujaMia : c.burbujaSuya}>
                    {t.texto}
                    {t.quien === "assistant" && t.consultas && t.consultas.length > 0 && (
                      <div className={c.chips}>
                        {t.consultas.map((q, k) => <span key={k} className={c.chip}>{q}</span>)}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {cargando && (
                <div style={{ fontSize: 12.5, color: meta.color, fontStyle: "italic" }}>
                  consultando los datos…
                </div>
              )}
              {error && (
                <div style={{ fontSize: 12.5, color: "#FCA5A5", fontWeight: 600, marginTop: 6 }}>
                  {error}
                </div>
              )}
              <div ref={finRef} />
            </div>

            <div className={c.pie}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <textarea
                  className={c.input}
                  rows={2}
                  placeholder="¿Cómo viene el mes?"
                  value={texto}
                  disabled={cargando}
                  onChange={(e) => setTexto(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter manda; Shift+Enter hace salto de línea.
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); preguntar(); }
                  }}
                />
                <button
                  className={c.iconBtn}
                  style={{ color: texto.trim() && !cargando ? meta.color : "#475569", paddingBottom: 10 }}
                  onClick={() => preguntar()}
                  disabled={cargando || !texto.trim()}
                  title="Preguntar"
                >
                  <Ico name="send" size={18} />
                </button>
              </div>
              <div className={c.nota}>
                Sólo lectura: consulta y responde. No puede pausar campañas ni modificar nada, y si
                una consulta falla te lo dice en vez de estimar el número.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
